package com.example.event_management_system.ui.screens.admin

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.Send
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowUp
import androidx.compose.material.icons.filled.MoreVert
import androidx.compose.material.icons.outlined.BarChart
import androidx.compose.material.icons.outlined.Block
import androidx.compose.material.icons.outlined.Group
import androidx.compose.material.icons.outlined.HealthAndSafety
import androidx.compose.material.icons.outlined.Mail
import androidx.compose.material.icons.outlined.Place
import androidx.compose.material.icons.outlined.Power
import androidx.compose.material.icons.outlined.Shield
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.AssistChip
import androidx.compose.material3.AssistChipDefaults
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Snackbar
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Switch
import androidx.compose.material3.Tab
import androidx.compose.material3.TabRow
import androidx.compose.material3.TabRowDefaults
import androidx.compose.material3.TabRowDefaults.tabIndicatorOffset
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.koin.getScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.example.event_management_system.data.models.EmailWorkspace
import com.example.event_management_system.data.models.Location
import com.example.event_management_system.data.models.Section
import com.example.event_management_system.data.models.User
import com.example.event_management_system.data.models.UserRole
import androidx.compose.material3.Checkbox
import androidx.compose.material3.FilterChip
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.ui.text.input.KeyboardType
import com.example.event_management_system.ui.components.common.LoadingIndicator
import com.example.event_management_system.ui.theme.Dimens

class AdminDashboardScreen : Screen {

    @OptIn(ExperimentalMaterial3Api::class)
    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val screenModel = getScreenModel<AdminDashboardScreenModel>()
        val state by screenModel.state.collectAsState()
        val snackbarHostState = remember { SnackbarHostState() }

        val message = state.message ?: state.error
        LaunchedEffect(message) {
            if (message != null) {
                snackbarHostState.showSnackbar(message)
                screenModel.clearMessages()
            }
        }

        Scaffold(
            topBar = {
                TopAppBar(
                    title = { Text("Admin", fontWeight = FontWeight.SemiBold) },
                    navigationIcon = {
                        IconButton(onClick = { navigator.pop() }) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                        }
                    },
                    colors = TopAppBarDefaults.topAppBarColors(
                        containerColor = MaterialTheme.colorScheme.surface,
                        titleContentColor = MaterialTheme.colorScheme.onSurface
                    )
                )
            },
            snackbarHost = { SnackbarHost(snackbarHostState) { Snackbar(snackbarData = it) } },
            containerColor = MaterialTheme.colorScheme.surface
        ) { padding ->
            when {
                state.isLoading -> Box(
                    Modifier.fillMaxSize().padding(padding),
                    Alignment.Center
                ) { LoadingIndicator() }

                state.accessDenied -> AccessDeniedView(modifier = Modifier.padding(padding))

                else -> Column(modifier = Modifier.fillMaxSize().padding(padding)) {
                    HeroHeader(ownerName = state.currentUser?.displayName.orEmpty())
                    AdminTabRow(selected = state.selectedTab, onSelect = screenModel::selectTab)
                    Box(modifier = Modifier.weight(1f).fillMaxSize()) {
                        when (state.selectedTab) {
                            AdminTab.EMAIL_WORKSPACES -> EmailWorkspacesTab(
                                state = state,
                                onSave = screenModel::saveWorkspace,
                                onDelete = screenModel::deleteWorkspace,
                                onToggleEnabled = screenModel::toggleEnabled,
                                onTestSend = screenModel::sendTest
                            )
                            AdminTab.TEAM_MEMBERS -> TeamMembersTab(
                                state = state,
                                onSave = screenModel::saveTeamMember,
                                onSetActive = screenModel::setUserActive,
                                onDelete = screenModel::deleteTeamMember
                            )
                            AdminTab.LOCATIONS -> LocationsTab(
                                state = state,
                                onSaveLocation = screenModel::saveLocation,
                                onDeleteLocation = screenModel::deleteLocation,
                                onSaveSection = screenModel::saveSection,
                                onDeleteSection = screenModel::deleteSection,
                                onEditEmailTemplate = screenModel::openEmailTemplate,
                                onDismissEmailTemplate = screenModel::dismissEmailTemplate,
                                onSaveEmailTemplate = screenModel::saveEmailTemplate
                            )
                            AdminTab.REPORTS -> ComingSoonView(
                                title = "Reports",
                                description = "Email activity, event metrics, and CSV exports — coming in Phase 3."
                            )
                        }
                    }
                }
            }
        }
    }
}

// ─────────────────────────── Header & Tabs ───────────────────────────

@Composable
private fun HeroHeader(ownerName: String) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(MaterialTheme.colorScheme.surface)
            .padding(horizontal = Dimens.paddingXLarge, vertical = Dimens.paddingLarge)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(
                modifier = Modifier
                    .size(44.dp)
                    .background(
                        MaterialTheme.colorScheme.primaryContainer,
                        RoundedCornerShape(12.dp)
                    ),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    Icons.Outlined.Shield,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.onPrimaryContainer
                )
            }
            Spacer(Modifier.size(Dimens.spacingMedium))
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = "Admin Dashboard",
                    style = MaterialTheme.typography.headlineMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
                Text(
                    text = if (ownerName.isNotBlank()) "Signed in as $ownerName"
                    else "Owner-only configuration",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            OwnerBadge()
        }
    }
    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
}

@Composable
private fun OwnerBadge() {
    Row(
        modifier = Modifier
            .background(
                MaterialTheme.colorScheme.tertiaryContainer,
                RoundedCornerShape(999.dp)
            )
            .padding(horizontal = 12.dp, vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        Icon(
            Icons.Filled.CheckCircle,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.onTertiaryContainer,
            modifier = Modifier.size(14.dp)
        )
        Text(
            text = "OWNER",
            style = MaterialTheme.typography.labelSmall,
            fontWeight = FontWeight.SemiBold,
            color = MaterialTheme.colorScheme.onTertiaryContainer
        )
    }
}

@Composable
private fun AdminTabRow(selected: AdminTab, onSelect: (AdminTab) -> Unit) {
    val tabs = listOf(
        AdminTab.EMAIL_WORKSPACES to Icons.Outlined.Mail,
        AdminTab.TEAM_MEMBERS     to Icons.Outlined.Group,
        AdminTab.LOCATIONS        to Icons.Outlined.Place,
        AdminTab.REPORTS          to Icons.Outlined.BarChart
    )
    val selectedIndex = tabs.indexOfFirst { it.first == selected }.coerceAtLeast(0)

    TabRow(
        selectedTabIndex = selectedIndex,
        containerColor = MaterialTheme.colorScheme.surface,
        contentColor = MaterialTheme.colorScheme.primary,
        indicator = { positions ->
            TabRowDefaults.SecondaryIndicator(
                modifier = Modifier.tabIndicatorOffset(positions[selectedIndex]),
                color = MaterialTheme.colorScheme.primary,
                height = 3.dp
            )
        }
    ) {
        tabs.forEach { (tab, icon) ->
            val isSelected = tab == selected
            Tab(
                selected = isSelected,
                onClick = { onSelect(tab) },
                icon = { Icon(icon, contentDescription = null, modifier = Modifier.size(20.dp)) },
                text = {
                    Text(
                        text = tab.title,
                        fontWeight = if (isSelected) FontWeight.SemiBold else FontWeight.Medium
                    )
                },
                selectedContentColor = MaterialTheme.colorScheme.primary,
                unselectedContentColor = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
}

// ─────────────────────────── Generic Views ───────────────────────────

@Composable
private fun AccessDeniedView(modifier: Modifier = Modifier) {
    Box(modifier = modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier.widthIn(max = 360.dp)
        ) {
            Icon(
                Icons.Outlined.Block,
                contentDescription = null,
                modifier = Modifier.size(56.dp),
                tint = MaterialTheme.colorScheme.error
            )
            Spacer(Modifier.height(Dimens.spacingMedium))
            Text(
                text = "Restricted area",
                style = MaterialTheme.typography.headlineSmall,
                fontWeight = FontWeight.SemiBold
            )
            Spacer(Modifier.height(Dimens.spacingSmall))
            Text(
                text = "Only an Owner can access the Admin dashboard.",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

@Composable
private fun ComingSoonView(title: String, description: String) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .padding(Dimens.paddingXLarge),
        contentAlignment = Alignment.Center
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier.widthIn(max = 420.dp)
        ) {
            Box(
                modifier = Modifier
                    .size(72.dp)
                    .background(MaterialTheme.colorScheme.surfaceVariant, CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    Icons.Outlined.Power,
                    contentDescription = null,
                    modifier = Modifier.size(32.dp),
                    tint = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            Spacer(Modifier.height(Dimens.spacingMedium))
            Text(
                text = title,
                style = MaterialTheme.typography.headlineSmall,
                fontWeight = FontWeight.SemiBold
            )
            Spacer(Modifier.height(Dimens.spacingSmall))
            Text(
                text = description,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

// ─────────────────────────── Email Workspaces Tab ───────────────────────────

@Composable
private fun EmailWorkspacesTab(
    state: AdminDashboardState,
    onSave: (EmailWorkspace) -> Unit,
    onDelete: (String) -> Unit,
    onToggleEnabled: (String, Boolean) -> Unit,
    onTestSend: (String, String, String?) -> Unit
) {
    var editing  by remember { mutableStateOf<EmailWorkspace?>(null) }
    var creating by remember { mutableStateOf(false) }
    var deleting by remember { mutableStateOf<EmailWorkspace?>(null) }
    var testing  by remember { mutableStateOf<EmailWorkspace?>(null) }
    var helpExpanded by remember { mutableStateOf(false) }

    val total    = state.workspaces.size
    val enabled  = state.workspaces.count { it.enabled }
    val disabled = total - enabled
    val healthLabel = when {
        total == 0   -> "Not configured"
        disabled == 0 -> "All healthy"
        else          -> "$disabled disabled"
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = Dimens.paddingXLarge, vertical = Dimens.paddingLarge)
    ) {
        // Stats row
        Row(horizontalArrangement = Arrangement.spacedBy(Dimens.spacingMedium)) {
            StatCard(
                modifier  = Modifier.weight(1f),
                icon      = Icons.Outlined.Mail,
                value     = total.toString(),
                label     = "Workspaces",
                tint      = MaterialTheme.colorScheme.primary
            )
            StatCard(
                modifier  = Modifier.weight(1f),
                icon      = Icons.Filled.CheckCircle,
                value     = enabled.toString(),
                label     = "Enabled",
                tint      = MaterialTheme.colorScheme.tertiary
            )
            StatCard(
                modifier  = Modifier.weight(1f),
                icon      = Icons.Outlined.Block,
                value     = disabled.toString(),
                label     = "Disabled",
                tint      = if (disabled == 0) MaterialTheme.colorScheme.onSurfaceVariant
                            else MaterialTheme.colorScheme.error
            )
            StatCard(
                modifier  = Modifier.weight(1f),
                icon      = Icons.Outlined.HealthAndSafety,
                value     = if (total == 0) "—" else if (disabled == 0) "OK" else "!",
                label     = healthLabel,
                tint      = when {
                    total == 0    -> MaterialTheme.colorScheme.onSurfaceVariant
                    disabled == 0 -> MaterialTheme.colorScheme.tertiary
                    else          -> MaterialTheme.colorScheme.error
                }
            )
        }

        Spacer(Modifier.height(Dimens.spacingXLarge))

        // Section header + Add button
        Row(verticalAlignment = Alignment.CenterVertically) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text  = "Email workspaces",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.SemiBold
                )
                Text(
                    text  = "Map each domain to a Google Workspace service account in Secret Manager.",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            Button(
                onClick  = { creating = true },
                enabled  = !state.isSavingWorkspace,
                shape    = RoundedCornerShape(12.dp)
            ) {
                Icon(Icons.Filled.Add, contentDescription = null, modifier = Modifier.size(18.dp))
                Spacer(Modifier.size(Dimens.spacingSmall))
                Text("Add workspace")
            }
        }

        Spacer(Modifier.height(Dimens.spacingLarge))

        when {
            state.isWorkspacesLoading -> Box(
                modifier = Modifier.fillMaxWidth().heightIn(min = 160.dp),
                contentAlignment = Alignment.Center
            ) { LoadingIndicator() }

            state.workspacesError != null -> ErrorBanner(state.workspacesError!!)

            state.workspaces.isEmpty() -> EmptyWorkspacesCard(onAdd = { creating = true })

            else -> Column(verticalArrangement = Arrangement.spacedBy(Dimens.spacingMedium)) {
                state.workspaces.forEach { ws ->
                    WorkspaceCard(
                        workspace = ws,
                        onEdit    = { editing = ws },
                        onDelete  = { deleting = ws },
                        onToggle  = { onToggleEnabled(ws.domain, it) },
                        onTest    = { testing = ws }
                    )
                }
            }
        }

        Spacer(Modifier.height(Dimens.spacingXLarge))
        SetupHintCard(expanded = helpExpanded, onToggle = { helpExpanded = !helpExpanded })
        Spacer(Modifier.height(Dimens.spacingXLarge))
    }

    // Dialogs
    if (creating) {
        WorkspaceFormDialog(
            initial        = null,
            existingDomains = state.workspaces.map { it.domain }.toSet(),
            onDismiss      = { creating = false },
            onSubmit       = { onSave(it); creating = false },
            isSaving       = state.isSavingWorkspace
        )
    }

    editing?.let { ws ->
        WorkspaceFormDialog(
            initial         = ws,
            existingDomains = state.workspaces.map { it.domain }.toSet(),
            onDismiss       = { editing = null },
            onSubmit        = { onSave(it); editing = null },
            isSaving        = state.isSavingWorkspace
        )
    }

    deleting?.let { ws ->
        AlertDialog(
            onDismissRequest = { deleting = null },
            icon    = { Icon(Icons.Filled.Delete, contentDescription = null) },
            title   = { Text("Remove \"${ws.domain}\"?") },
            text    = {
                Text(
                    "Sends from this domain will stop. The Secret Manager secret " +
                    "\"${ws.secretName}\" is NOT deleted — remove it via gcloud if no longer needed."
                )
            },
            confirmButton = {
                Button(
                    onClick = { onDelete(ws.domain); deleting = null },
                    colors  = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.error,
                        contentColor   = MaterialTheme.colorScheme.onError
                    )
                ) { Text("Remove") }
            },
            dismissButton = { TextButton(onClick = { deleting = null }) { Text("Cancel") } }
        )
    }

    testing?.let { ws ->
        TestSendDialog(
            workspace = ws,
            isSending = state.isTestSending,
            onDismiss = { testing = null },
            onSend    = { to, from -> onTestSend(ws.domain, to, from); testing = null }
        )
    }
}

// ─────────────────────────── Locations Tab ───────────────────────────

@Composable
private fun LocationsTab(
    state: AdminDashboardState,
    onSaveLocation: (Location) -> Unit,
    onDeleteLocation: (String) -> Unit,
    onSaveSection: (Section) -> Unit,
    onDeleteSection: (String) -> Unit,
    onEditEmailTemplate: (Location) -> Unit,
    onDismissEmailTemplate: () -> Unit,
    onSaveEmailTemplate: (String) -> Unit
) {
    var creatingVenue by remember { mutableStateOf(false) }
    var editingVenue   by remember { mutableStateOf<Location?>(null) }
    var deletingVenue  by remember { mutableStateOf<Location?>(null) }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = Dimens.paddingXLarge, vertical = Dimens.paddingLarge)
    ) {
        val totalSections = state.sectionsMap.values.sumOf { it.size }
        Row(horizontalArrangement = Arrangement.spacedBy(Dimens.spacingMedium)) {
            StatCard(
                modifier = Modifier.weight(1f),
                icon     = Icons.Outlined.Place,
                value    = state.locations.size.toString(),
                label    = "Venues",
                tint     = MaterialTheme.colorScheme.primary
            )
            StatCard(
                modifier = Modifier.weight(1f),
                icon     = Icons.Outlined.Place,
                value    = totalSections.toString(),
                label    = "Areas",
                tint     = MaterialTheme.colorScheme.tertiary
            )
        }

        Spacer(Modifier.height(Dimens.spacingXLarge))

        Row(verticalAlignment = Alignment.CenterVertically) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text       = "Venues & Reservation Areas",
                    style      = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.SemiBold
                )
                Text(
                    text  = "Manage bar venues and the specific areas (booths, rooms) within each venue.",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            Button(
                onClick = { creatingVenue = true },
                enabled = !state.isSavingLocation,
                shape   = RoundedCornerShape(12.dp)
            ) {
                Icon(Icons.Filled.Add, contentDescription = null, modifier = Modifier.size(18.dp))
                Spacer(Modifier.size(Dimens.spacingSmall))
                Text("Add venue")
            }
        }

        Spacer(Modifier.height(Dimens.spacingLarge))

        when {
            state.isLocationsLoading -> Box(
                modifier = Modifier.fillMaxWidth().heightIn(min = 160.dp),
                contentAlignment = Alignment.Center
            ) { LoadingIndicator() }

            state.locationsError != null -> ErrorBanner(state.locationsError!!)

            state.locations.isEmpty() -> EmptyVenuesCard(onAdd = { creatingVenue = true })

            else -> Column(verticalArrangement = Arrangement.spacedBy(Dimens.spacingMedium)) {
                state.locations.forEach { location ->
                    VenueCard(
                        location        = location,
                        sections        = state.sectionsMap[location.id] ?: emptyList(),
                        onEdit          = { editingVenue = location },
                        onDelete        = { deletingVenue = location },
                        onSaveSection   = onSaveSection,
                        onDeleteSection = onDeleteSection,
                        onEditEmailTemplate = { onEditEmailTemplate(location) }
                    )
                }
            }
        }

        Spacer(Modifier.height(Dimens.spacingXLarge))
    }

    if (creatingVenue) {
        VenueFormDialog(
            initial   = null,
            onDismiss = { creatingVenue = false },
            onSubmit  = { onSaveLocation(it); creatingVenue = false },
            isSaving  = state.isSavingLocation
        )
    }

    editingVenue?.let { venue ->
        VenueFormDialog(
            initial   = venue,
            onDismiss = { editingVenue = null },
            onSubmit  = { onSaveLocation(it); editingVenue = null },
            isSaving  = state.isSavingLocation
        )
    }

    state.editingEmailTemplate?.let { location ->
        EmailTemplateDialog(
            location  = location,
            isSaving  = state.isSavingTemplate,
            onDismiss = onDismissEmailTemplate,
            onSubmit  = onSaveEmailTemplate
        )
    }

    deletingVenue?.let { venue ->
        AlertDialog(
            onDismissRequest = { deletingVenue = null },
            icon    = { Icon(Icons.Filled.Delete, contentDescription = null) },
            title   = { Text("Delete \"${venue.name}\"?") },
            text    = { Text("The venue will be removed. Existing events referencing it are not affected.") },
            confirmButton = {
                Button(
                    onClick = { onDeleteLocation(venue.id); deletingVenue = null },
                    colors  = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.error,
                        contentColor   = MaterialTheme.colorScheme.onError
                    )
                ) { Text("Delete") }
            },
            dismissButton = { TextButton(onClick = { deletingVenue = null }) { Text("Cancel") } }
        )
    }
}

@Composable
private fun VenueCard(
    location: Location,
    sections: List<Section>,
    onEdit: () -> Unit,
    onDelete: () -> Unit,
    onSaveSection: (Section) -> Unit,
    onDeleteSection: (String) -> Unit,
    onEditEmailTemplate: () -> Unit
) {
    var expanded       by remember { mutableStateOf(true) }
    var menuOpen       by remember { mutableStateOf(false) }
    var addingSection  by remember { mutableStateOf(false) }
    var editingSection by remember { mutableStateOf<Section?>(null) }
    var deletingSection by remember { mutableStateOf<Section?>(null) }

    Card(
        modifier  = Modifier.fillMaxWidth(),
        shape     = RoundedCornerShape(20.dp),
        colors    = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        border    = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
    ) {
        Column {
            Row(
                modifier          = Modifier.padding(Dimens.paddingLarge),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier            = Modifier
                        .size(40.dp)
                        .background(MaterialTheme.colorScheme.primaryContainer, RoundedCornerShape(12.dp)),
                    contentAlignment    = Alignment.Center
                ) {
                    Icon(
                        Icons.Outlined.Place,
                        contentDescription = null,
                        tint    = MaterialTheme.colorScheme.onPrimaryContainer,
                        modifier = Modifier.size(22.dp)
                    )
                }
                Spacer(Modifier.size(Dimens.spacingMedium))
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text       = location.name,
                        style      = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.SemiBold,
                        maxLines   = 1,
                        overflow   = TextOverflow.Ellipsis
                    )
                    if (!location.address.isNullOrBlank()) {
                        Text(
                            text  = location.address,
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                    Text(
                        text  = "${sections.size} area${if (sections.size == 1) "" else "s"}",
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.primary
                    )
                }
                Box {
                    IconButton(onClick = { menuOpen = true }) {
                        Icon(Icons.Filled.MoreVert, contentDescription = "Actions")
                    }
                    DropdownMenu(expanded = menuOpen, onDismissRequest = { menuOpen = false }) {
                        DropdownMenuItem(
                            text         = { Text("Edit venue") },
                            leadingIcon  = { Icon(Icons.Filled.Edit, contentDescription = null) },
                            onClick      = { menuOpen = false; onEdit() }
                        )
                        DropdownMenuItem(
                            text         = { Text("Email template") },
                            leadingIcon  = { Icon(Icons.Outlined.Mail, contentDescription = null) },
                            onClick      = { menuOpen = false; onEditEmailTemplate() }
                        )
                        DropdownMenuItem(
                            text         = { Text("Delete venue", color = MaterialTheme.colorScheme.error) },
                            leadingIcon  = { Icon(Icons.Filled.Delete, contentDescription = null, tint = MaterialTheme.colorScheme.error) },
                            onClick      = { menuOpen = false; onDelete() }
                        )
                    }
                }
                IconButton(onClick = { expanded = !expanded }) {
                    Icon(
                        if (expanded) Icons.Filled.KeyboardArrowUp else Icons.Filled.KeyboardArrowDown,
                        contentDescription = if (expanded) "Collapse" else "Expand"
                    )
                }
            }

            AnimatedVisibility(visible = expanded) {
                Column {
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    Row(
                        modifier          = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = Dimens.paddingLarge, vertical = Dimens.spacingSmall),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text     = "Reservation Areas",
                            style    = MaterialTheme.typography.labelLarge,
                            color    = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.weight(1f)
                        )
                        TextButton(onClick = { addingSection = true }) {
                            Icon(Icons.Filled.Add, contentDescription = null, modifier = Modifier.size(16.dp))
                            Spacer(Modifier.size(4.dp))
                            Text("Add area")
                        }
                    }
                    if (sections.isEmpty()) {
                        Text(
                            text     = "No areas yet — add booth numbers, rooms, sides of the bar, etc.",
                            style    = MaterialTheme.typography.bodySmall,
                            color    = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.padding(horizontal = Dimens.paddingLarge).padding(bottom = Dimens.paddingMedium)
                        )
                    } else {
                        Column(modifier = Modifier.padding(bottom = Dimens.spacingSmall)) {
                            sections.forEach { section ->
                                SectionRow(
                                    section  = section,
                                    onEdit   = { editingSection = section },
                                    onDelete = { deletingSection = section }
                                )
                            }
                        }
                    }
                }
            }
        }
    }

    if (addingSection) {
        SectionFormDialog(
            locationId = location.id,
            initial    = null,
            onDismiss  = { addingSection = false },
            onSubmit   = { onSaveSection(it); addingSection = false }
        )
    }

    editingSection?.let { sec ->
        SectionFormDialog(
            locationId = location.id,
            initial    = sec,
            onDismiss  = { editingSection = null },
            onSubmit   = { onSaveSection(it); editingSection = null }
        )
    }

    deletingSection?.let { sec ->
        AlertDialog(
            onDismissRequest = { deletingSection = null },
            title   = { Text("Delete \"${sec.name}\"?") },
            text    = { Text("This area will be removed from the reservation location list.") },
            confirmButton = {
                Button(
                    onClick = { onDeleteSection(sec.id); deletingSection = null },
                    colors  = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.error,
                        contentColor   = MaterialTheme.colorScheme.onError
                    )
                ) { Text("Delete") }
            },
            dismissButton = { TextButton(onClick = { deletingSection = null }) { Text("Cancel") } }
        )
    }
}

@Composable
private fun SectionRow(section: Section, onEdit: () -> Unit, onDelete: () -> Unit) {
    Row(
        modifier          = Modifier
            .fillMaxWidth()
            .padding(horizontal = Dimens.paddingLarge, vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .size(8.dp)
                .background(MaterialTheme.colorScheme.primary, CircleShape)
        )
        Spacer(Modifier.width(Dimens.spacingMedium))
        Text(
            text     = section.name,
            style    = MaterialTheme.typography.bodyMedium,
            modifier = Modifier.weight(1f)
        )
        IconButton(onClick = onEdit, modifier = Modifier.size(32.dp)) {
            Icon(Icons.Filled.Edit, contentDescription = "Edit area", modifier = Modifier.size(16.dp), tint = MaterialTheme.colorScheme.onSurfaceVariant)
        }
        IconButton(onClick = onDelete, modifier = Modifier.size(32.dp)) {
            Icon(Icons.Filled.Delete, contentDescription = "Delete area", modifier = Modifier.size(16.dp), tint = MaterialTheme.colorScheme.error)
        }
    }
}

@Composable
private fun EmptyVenuesCard(onAdd: () -> Unit) {
    Card(
        modifier  = Modifier.fillMaxWidth(),
        shape     = RoundedCornerShape(20.dp),
        colors    = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        border    = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
    ) {
        Column(
            modifier            = Modifier.fillMaxWidth().padding(Dimens.paddingXLarge),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Box(
                modifier         = Modifier.size(64.dp).background(MaterialTheme.colorScheme.primaryContainer, CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Icon(Icons.Outlined.Place, contentDescription = null, modifier = Modifier.size(28.dp), tint = MaterialTheme.colorScheme.onPrimaryContainer)
            }
            Spacer(Modifier.height(Dimens.spacingMedium))
            Text("No venues yet", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.SemiBold)
            Spacer(Modifier.height(Dimens.spacingXxSmall))
            Text(
                text     = "Add your bar venues here. For each venue you can then add specific reservation areas like booths, rooms, or sections.",
                style    = MaterialTheme.typography.bodyMedium,
                color    = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.widthIn(max = 400.dp)
            )
            Spacer(Modifier.height(Dimens.spacingLarge))
            Button(onClick = onAdd, shape = RoundedCornerShape(12.dp)) {
                Icon(Icons.Filled.Add, contentDescription = null, modifier = Modifier.size(18.dp))
                Spacer(Modifier.size(Dimens.spacingSmall))
                Text("Add the first venue")
            }
        }
    }
}

@Composable
private fun VenueFormDialog(
    initial  : Location?,
    onDismiss: () -> Unit,
    onSubmit : (Location) -> Unit,
    isSaving : Boolean
) {
    val isEdit = initial != null
    var name    by remember { mutableStateOf(initial?.name.orEmpty()) }
    var address by remember { mutableStateOf(initial?.address.orEmpty()) }
    var phone   by remember { mutableStateOf(initial?.phone.orEmpty()) }
    var logoUrl by remember { mutableStateOf(initial?.logoUrl.orEmpty()) }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(if (isEdit) "Edit venue" else "Add venue") },
        text  = {
            Column(modifier = Modifier.verticalScroll(rememberScrollState())) {
                OutlinedTextField(
                    value         = name,
                    onValueChange = { name = it },
                    label         = { Text("Venue name *") },
                    placeholder   = { Text("e.g. Bamboo Hut") },
                    singleLine    = true,
                    modifier      = Modifier.fillMaxWidth()
                )
                Spacer(Modifier.height(Dimens.spacingSmall))
                OutlinedTextField(
                    value         = address,
                    onValueChange = { address = it },
                    label         = { Text("Address") },
                    singleLine    = true,
                    modifier      = Modifier.fillMaxWidth()
                )
                Spacer(Modifier.height(Dimens.spacingSmall))
                OutlinedTextField(
                    value         = phone,
                    onValueChange = { phone = it },
                    label         = { Text("Phone") },
                    singleLine    = true,
                    modifier      = Modifier.fillMaxWidth()
                )
                Spacer(Modifier.height(Dimens.spacingSmall))
                OutlinedTextField(
                    value         = logoUrl,
                    onValueChange = { logoUrl = it },
                    label         = { Text("Logo URL") },
                    placeholder   = { Text("https://…  (used in emails & sidebar)") },
                    singleLine    = true,
                    modifier      = Modifier.fillMaxWidth()
                )
                if (logoUrl.isNotBlank()) {
                    Spacer(Modifier.height(Dimens.spacingSmall))
                    Text(
                        text  = "Logo will appear at the top of confirmation emails and in the sidebar.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    onSubmit(
                        (initial ?: Location()).copy(
                            name    = name.trim(),
                            address = address.trim().ifBlank { null },
                            phone   = phone.trim().ifBlank { null },
                            logoUrl = logoUrl.trim().ifBlank { null }
                        )
                    )
                },
                enabled = name.isNotBlank() && !isSaving
            ) { Text(if (isSaving) "Saving…" else if (isEdit) "Save" else "Add") }
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text("Cancel") } }
    )
}

@Composable
private fun EmailTemplateDialog(
    location : Location,
    isSaving : Boolean,
    onDismiss: () -> Unit,
    onSubmit : (bodyTemplate: String) -> Unit
) {
    var bodyTemplate  by remember(location.id) { mutableStateOf(location.emailBodyTemplate.orEmpty()) }
    var showPlaceholders by remember { mutableStateOf(false) }

    val placeholders = listOf(
        "{{name}}" to "Customer name",
        "{{location}}" to "Venue name",
        "{{date}}" to "Event date",
        "{{time}}" to "Start – end time",
        "{{guests}}" to "Guest count",
        "{{cost}}" to "Total cost",
        "{{deposit}}" to "Deposit",
        "{{status}}" to "Event status",
        "{{purpose}}" to "Event purpose (raw text)",
        "{{purposeBlock}}" to "\"Purpose: …\" line or empty",
        "{{signature}}" to "Signature line"
    )

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Email Template — ${location.name}") },
        text  = {
            Column(modifier = Modifier.verticalScroll(rememberScrollState())) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text  = "Full message body",
                        style = MaterialTheme.typography.labelLarge
                    )
                    TextButton(onClick = { bodyTemplate = DEFAULT_EMAIL_TEMPLATE }) {
                        Text("Reset to default")
                    }
                }
                Spacer(Modifier.height(Dimens.spacingSmall))
                OutlinedTextField(
                    value         = bodyTemplate,
                    onValueChange = { bodyTemplate = it },
                    placeholder   = { Text("Leave blank to use the default template") },
                    minLines      = 12,
                    modifier      = Modifier.fillMaxWidth(),
                    textStyle     = MaterialTheme.typography.bodySmall
                )
                Spacer(Modifier.height(Dimens.spacingSmall))
                TextButton(
                    onClick = { showPlaceholders = !showPlaceholders },
                    contentPadding = androidx.compose.foundation.layout.PaddingValues(0.dp)
                ) {
                    Text(
                        if (showPlaceholders) "Hide placeholders" else "Available placeholders",
                        style = MaterialTheme.typography.labelSmall
                    )
                    Icon(
                        if (showPlaceholders) Icons.Filled.KeyboardArrowUp else Icons.Filled.KeyboardArrowDown,
                        contentDescription = null,
                        modifier = Modifier.size(16.dp)
                    )
                }
                if (showPlaceholders) {
                    Spacer(Modifier.height(4.dp))
                    placeholders.forEach { (token, desc) ->
                        Row(modifier = Modifier.padding(vertical = 1.dp)) {
                            Text(
                                text  = token,
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.primary,
                                modifier = Modifier.width(140.dp)
                            )
                            Text(
                                text  = desc,
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                    Spacer(Modifier.height(Dimens.spacingSmall))
                }
            }
        },
        confirmButton = {
            Button(
                onClick  = { onSubmit(bodyTemplate) },
                enabled  = !isSaving
            ) { Text(if (isSaving) "Saving…" else "Save") }
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text("Cancel") } }
    )
}

private const val DEFAULT_EMAIL_TEMPLATE = """Hi {{name}},

Please find the confirmation details for your upcoming event at {{location}} below:

Location: {{location}}
Date: {{date}}
Time: {{time}}
Guest Count: {{guests}}
{{purposeBlock}}
Total Cost: {{cost}}
Deposit: {{deposit}}
Status: {{status}}

Thanks again for booking your party at {{location}}, we are here to help you host a great event. Please reply if you have any questions regarding the details above.


See you soon,


{{signature}}"""

@Composable
private fun SectionFormDialog(
    locationId: String,
    initial   : Section?,
    onDismiss : () -> Unit,
    onSubmit  : (Section) -> Unit
) {
    val isEdit = initial != null
    var name by remember { mutableStateOf(initial?.name.orEmpty()) }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(if (isEdit) "Edit area" else "Add reservation area") },
        text  = {
            OutlinedTextField(
                value         = name,
                onValueChange = { name = it },
                label         = { Text("Area name *") },
                placeholder   = { Text("e.g. Booth 1, Jungle Room, Bar Side") },
                singleLine    = true,
                modifier      = Modifier.fillMaxWidth()
            )
        },
        confirmButton = {
            Button(
                onClick = {
                    onSubmit(
                        (initial ?: Section(locationId = locationId)).copy(
                            name       = name.trim(),
                            locationId = locationId
                        )
                    )
                },
                enabled = name.isNotBlank()
            ) { Text(if (isEdit) "Save" else "Add") }
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text("Cancel") } }
    )
}

// ─────────────────────────── Cards & Components ───────────────────────────

@Composable
private fun StatCard(
    modifier : Modifier = Modifier,
    icon     : ImageVector,
    value    : String,
    label    : String,
    tint     : Color
) {
    Card(
        modifier  = modifier,
        shape     = RoundedCornerShape(12.dp),
        colors    = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        border    = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
    ) {
        Row(
            modifier = Modifier.padding(12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(Dimens.spacingSmall)
        ) {
            Box(
                modifier = Modifier
                    .size(32.dp)
                    .background(tint.copy(alpha = 0.12f), RoundedCornerShape(8.dp)),
                contentAlignment = Alignment.Center
            ) {
                Icon(icon, contentDescription = null, tint = tint, modifier = Modifier.size(18.dp))
            }
            Column {
                Text(
                    text  = value,
                    style = MaterialTheme.typography.headlineMedium,
                    fontWeight = FontWeight.Bold
                )
                Text(
                    text  = label,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
}

@Composable
private fun WorkspaceCard(
    workspace : EmailWorkspace,
    onEdit    : () -> Unit,
    onDelete  : () -> Unit,
    onToggle  : (Boolean) -> Unit,
    onTest    : () -> Unit
) {
    var menuOpen by remember { mutableStateOf(false) }
    val statusColor by animateColorAsState(
        targetValue = if (workspace.enabled) MaterialTheme.colorScheme.tertiary
                      else MaterialTheme.colorScheme.outline,
        label = "statusDot"
    )

    Card(
        modifier  = Modifier.fillMaxWidth(),
        shape     = RoundedCornerShape(20.dp),
        colors    = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        border    = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
    ) {
        Column(modifier = Modifier.padding(Dimens.paddingLarge)) {
            // Domain row
            Row(verticalAlignment = Alignment.CenterVertically) {
                Box(
                    modifier = Modifier
                        .size(10.dp)
                        .background(statusColor, CircleShape)
                )
                Spacer(Modifier.size(Dimens.spacingMedium))
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text  = workspace.domain,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.SemiBold,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    if (workspace.fromName.isNotBlank()) {
                        Text(
                            text  = workspace.fromName,
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                }
                Switch(checked = workspace.enabled, onCheckedChange = onToggle)
                Spacer(Modifier.size(Dimens.spacingXSmall))
                Box {
                    IconButton(onClick = { menuOpen = true }) {
                        Icon(Icons.Filled.MoreVert, contentDescription = "Actions")
                    }
                    DropdownMenu(
                        expanded = menuOpen,
                        onDismissRequest = { menuOpen = false }
                    ) {
                        DropdownMenuItem(
                            text = { Text("Send test email") },
                            leadingIcon = {
                                Icon(Icons.AutoMirrored.Filled.Send, contentDescription = null)
                            },
                            onClick = { menuOpen = false; onTest() }
                        )
                        DropdownMenuItem(
                            text = { Text("Edit") },
                            leadingIcon = {
                                Icon(Icons.Filled.Edit, contentDescription = null)
                            },
                            onClick = { menuOpen = false; onEdit() }
                        )
                        DropdownMenuItem(
                            text = {
                                Text("Remove", color = MaterialTheme.colorScheme.error)
                            },
                            leadingIcon = {
                                Icon(
                                    Icons.Filled.Delete,
                                    contentDescription = null,
                                    tint = MaterialTheme.colorScheme.error
                                )
                            },
                            onClick = { menuOpen = false; onDelete() }
                        )
                    }
                }
            }

            Spacer(Modifier.height(Dimens.spacingMedium))

            // Metadata chips
            Row(horizontalArrangement = Arrangement.spacedBy(Dimens.spacingSmall)) {
                MetadataChip("Secret",  workspace.secretName.ifBlank { "—" })
                MetadataChip("Version", workspace.secretVersion.ifBlank { "latest" })
                MetadataChip(
                    label      = "Status",
                    value      = if (workspace.enabled) "Active" else "Off",
                    valueColor = statusColor
                )
            }
        }
    }
}

@Composable
private fun MetadataChip(label: String, value: String, valueColor: Color? = null) {
    Row(
        modifier = Modifier
            .background(MaterialTheme.colorScheme.surfaceVariant, RoundedCornerShape(8.dp))
            .padding(horizontal = 10.dp, vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text  = label,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Spacer(Modifier.size(6.dp))
        Text(
            text       = value,
            style      = MaterialTheme.typography.labelMedium,
            color      = valueColor ?: MaterialTheme.colorScheme.onSurface,
            fontFamily = FontFamily.Monospace,
            fontWeight = FontWeight.Medium,
            maxLines   = 1,
            overflow   = TextOverflow.Ellipsis
        )
    }
}

@Composable
private fun EmptyWorkspacesCard(onAdd: () -> Unit) {
    Card(
        modifier  = Modifier.fillMaxWidth(),
        shape     = RoundedCornerShape(20.dp),
        colors    = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        border    = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(Dimens.paddingXLarge),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Box(
                modifier = Modifier
                    .size(64.dp)
                    .background(MaterialTheme.colorScheme.primaryContainer, CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    Icons.Outlined.Mail,
                    contentDescription = null,
                    modifier = Modifier.size(28.dp),
                    tint = MaterialTheme.colorScheme.onPrimaryContainer
                )
            }
            Spacer(Modifier.height(Dimens.spacingMedium))
            Text(
                text  = "No workspaces configured yet",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.SemiBold
            )
            Spacer(Modifier.height(Dimens.spacingXxSmall))
            Text(
                text  = "Add a workspace once you've created the GCP service account, " +
                        "enabled Domain-Wide Delegation, and uploaded the JSON key to Secret Manager.",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.widthIn(max = 480.dp)
            )
            Spacer(Modifier.height(Dimens.spacingLarge))
            Button(onClick = onAdd, shape = RoundedCornerShape(12.dp)) {
                Icon(Icons.Filled.Add, contentDescription = null, modifier = Modifier.size(18.dp))
                Spacer(Modifier.size(Dimens.spacingSmall))
                Text("Add the first workspace")
            }
        }
    }
}

@Composable
private fun SetupHintCard(expanded: Boolean, onToggle: () -> Unit) {
    Card(
        modifier  = Modifier.fillMaxWidth(),
        shape     = RoundedCornerShape(16.dp),
        colors    = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
    ) {
        Column(modifier = Modifier.padding(Dimens.paddingMedium)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    Icons.Outlined.HealthAndSafety,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.onSurfaceVariant
                )
                Spacer(Modifier.size(Dimens.spacingSmall))
                Text(
                    text  = "Before adding a workspace",
                    modifier = Modifier.weight(1f),
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.SemiBold,
                    color = MaterialTheme.colorScheme.onSurface
                )
                TextButton(onClick = onToggle) {
                    Text(if (expanded) "Hide" else "Show steps")
                }
            }
            if (expanded) {
                Spacer(Modifier.height(Dimens.spacingSmall))
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                Spacer(Modifier.height(Dimens.spacingSmall))
                Text(
                    text = buildString {
                        appendLine("1. Create a GCP service account and enable Domain-Wide Delegation.")
                        appendLine("2. Authorize the Client ID with scope https://www.googleapis.com/auth/gmail.send in the Workspace admin console.")
                        appendLine("3. Upload the JSON key to Secret Manager:")
                        appendLine("       gcloud secrets create gmail-sa-<domain> \\")
                        appendLine("         --replication-policy=automatic --data-file=key.json")
                        appendLine("4. Grant the Cloud Function service account access:")
                        appendLine("       gcloud secrets add-iam-policy-binding gmail-sa-<domain> \\")
                        appendLine("         --member=serviceAccount:<fn-sa> --role=roles/secretmanager.secretAccessor")
                        append(  "5. Then come back here and click \"Add workspace\".")
                    },
                    style      = MaterialTheme.typography.bodySmall,
                    fontFamily = FontFamily.Monospace,
                    color      = MaterialTheme.colorScheme.onSurface
                )
            }
        }
    }
}

@Composable
private fun ErrorBanner(message: String) {
    Card(
        modifier  = Modifier.fillMaxWidth(),
        shape     = RoundedCornerShape(12.dp),
        colors    = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.errorContainer)
    ) {
        Row(
            modifier = Modifier.padding(Dimens.paddingMedium),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(Icons.Outlined.Block, contentDescription = null, tint = MaterialTheme.colorScheme.onErrorContainer)
            Spacer(Modifier.size(Dimens.spacingMedium))
            Text(text = message, color = MaterialTheme.colorScheme.onErrorContainer)
        }
    }
}

// ─────────────────────────── Dialogs ───────────────────────────

@Composable
private fun WorkspaceFormDialog(
    initial         : EmailWorkspace?,
    existingDomains : Set<String>,
    onDismiss       : () -> Unit,
    onSubmit        : (EmailWorkspace) -> Unit,
    isSaving        : Boolean
) {
    val isEdit = initial != null
    var domain        by remember { mutableStateOf(initial?.domain.orEmpty()) }
    var fromName      by remember { mutableStateOf(initial?.fromName.orEmpty()) }
    var secretName    by remember { mutableStateOf(initial?.secretName.orEmpty()) }
    var secretVersion by remember { mutableStateOf(initial?.secretVersion?.takeIf { it.isNotBlank() } ?: "latest") }
    var enabled       by remember { mutableStateOf(initial?.enabled ?: true) }

    val normalizedDomain = domain.trim().lowercase()
    val domainConflict   = !isEdit && normalizedDomain in existingDomains
    val canSubmit        = normalizedDomain.isNotBlank() && secretName.isNotBlank()
                           && !domainConflict && !isSaving

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(if (isEdit) "Edit workspace" else "Add workspace") },
        text  = {
            Column(modifier = Modifier.verticalScroll(rememberScrollState())) {
                OutlinedTextField(
                    value         = domain,
                    onValueChange = { if (!isEdit) domain = it },
                    label         = { Text("Domain") },
                    placeholder   = { Text("bamboohutsf.com") },
                    enabled       = !isEdit,
                    isError       = domainConflict,
                    supportingText = {
                        Text(if (domainConflict) "A workspace for this domain already exists."
                             else "Lowercase. Cannot be changed after creation.")
                    },
                    singleLine = true,
                    modifier   = Modifier.fillMaxWidth()
                )
                Spacer(Modifier.height(Dimens.spacingSmall))
                OutlinedTextField(
                    value         = fromName,
                    onValueChange = { fromName = it },
                    label         = { Text("From name") },
                    placeholder   = { Text("Bamboo Hut SF") },
                    singleLine    = true,
                    modifier      = Modifier.fillMaxWidth()
                )
                Spacer(Modifier.height(Dimens.spacingSmall))
                OutlinedTextField(
                    value          = secretName,
                    onValueChange  = { secretName = it },
                    label          = { Text("Secret Manager name") },
                    placeholder    = { Text("gmail-sa-bamboohutsf") },
                    supportingText = { Text("Must already exist in Google Secret Manager.") },
                    singleLine     = true,
                    modifier       = Modifier.fillMaxWidth()
                )
                Spacer(Modifier.height(Dimens.spacingSmall))
                OutlinedTextField(
                    value          = secretVersion,
                    onValueChange  = { secretVersion = it },
                    label          = { Text("Secret version") },
                    placeholder    = { Text("latest") },
                    supportingText = { Text("Use a number to pin to a specific version.") },
                    singleLine     = true,
                    modifier       = Modifier.fillMaxWidth()
                )
                Spacer(Modifier.height(Dimens.spacingMedium))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Switch(checked = enabled, onCheckedChange = { enabled = it })
                    Spacer(Modifier.size(Dimens.spacingSmall))
                    Text("Enabled")
                }
            }
        },
        confirmButton = {
            Button(
                onClick  = {
                    onSubmit(EmailWorkspace(
                        domain        = normalizedDomain,
                        fromName      = fromName.trim(),
                        secretName    = secretName.trim(),
                        secretVersion = secretVersion.trim().ifBlank { "latest" },
                        enabled       = enabled
                    ))
                },
                enabled = canSubmit
            ) { Text(if (isSaving) "Saving…" else if (isEdit) "Save" else "Add") }
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text("Cancel") } }
    )
}

@Composable
private fun TestSendDialog(
    workspace : EmailWorkspace,
    isSending : Boolean,
    onDismiss : () -> Unit,
    onSend    : (String, String?) -> Unit
) {
    var to           by remember { mutableStateOf("") }
    var fromOverride by remember { mutableStateOf("events@${workspace.domain}") }

    AlertDialog(
        onDismissRequest = onDismiss,
        icon  = { Icon(Icons.AutoMirrored.Filled.Send, contentDescription = null) },
        title = { Text("Send a test email") },
        text  = {
            Column {
                AssistChip(
                    onClick  = {},
                    enabled  = false,
                    label    = { Text(workspace.domain) },
                    colors   = AssistChipDefaults.assistChipColors(
                        disabledContainerColor = MaterialTheme.colorScheme.tertiaryContainer,
                        disabledLabelColor     = MaterialTheme.colorScheme.onTertiaryContainer
                    )
                )
                Spacer(Modifier.height(Dimens.spacingMedium))
                OutlinedTextField(
                    value          = fromOverride,
                    onValueChange  = { fromOverride = it },
                    label          = { Text("Send from") },
                    supportingText = { Text("Must be an existing mailbox on @${workspace.domain}.") },
                    singleLine     = true,
                    modifier       = Modifier.fillMaxWidth()
                )
                Spacer(Modifier.height(Dimens.spacingSmall))
                OutlinedTextField(
                    value         = to,
                    onValueChange = { to = it },
                    label         = { Text("Send to") },
                    placeholder   = { Text("you@example.com") },
                    singleLine    = true,
                    modifier      = Modifier.fillMaxWidth()
                )
            }
        },
        confirmButton = {
            Button(
                onClick  = { onSend(to.trim(), fromOverride.trim().ifBlank { null }) },
                enabled  = !isSending && to.contains('@')
            ) { Text(if (isSending) "Sending…" else "Send test") }
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text("Cancel") } }
    )
}

// ─────────────────────────── Team Members Tab ───────────────────────────

private fun initialsOfAdmin(displayName: String, email: String): String {
    val source = displayName.ifBlank { email.substringBefore('@') }
    val parts = source.trim().split(Regex("\\s+")).filter { it.isNotBlank() }
    return when {
        parts.isEmpty() -> "?"
        parts.size == 1 -> parts[0].take(2).uppercase()
        else -> (parts.first().first().toString() + parts.last().first().toString()).uppercase()
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun TeamMembersTab(
    state: AdminDashboardState,
    onSave: (User) -> Unit,
    onSetActive: (String, Boolean) -> Unit,
    onDelete: (String) -> Unit
) {
    var creating by remember { mutableStateOf(false) }
    var editing by remember { mutableStateOf<User?>(null) }
    var confirmToggle by remember { mutableStateOf<User?>(null) }
    var confirmDelete by remember { mutableStateOf<User?>(null) }
    var roleFilter by remember { mutableStateOf<UserRole?>(null) }

    val teamUsers = state.users.filter { it.role != UserRole.OWNER }
    val filtered = if (roleFilter == null) teamUsers else teamUsers.filter { it.role == roleFilter }
    val managers = teamUsers.count { it.role == UserRole.MANAGER }
    val staff = teamUsers.count { it.role == UserRole.STAFF }
    val inactive = teamUsers.count { !it.isActive }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = Dimens.paddingXLarge, vertical = Dimens.paddingLarge)
    ) {
        Row(horizontalArrangement = Arrangement.spacedBy(Dimens.spacingMedium)) {
            StatCard(Modifier.weight(1f), Icons.Outlined.Group, teamUsers.size.toString(), "Total", MaterialTheme.colorScheme.primary)
            StatCard(Modifier.weight(1f), Icons.Outlined.Shield, managers.toString(), "Managers", MaterialTheme.colorScheme.tertiary)
            StatCard(Modifier.weight(1f), Icons.Outlined.Group, staff.toString(), "Staff", MaterialTheme.colorScheme.secondary)
            StatCard(
                Modifier.weight(1f), Icons.Outlined.Block, inactive.toString(), "Inactive",
                if (inactive == 0) MaterialTheme.colorScheme.onSurfaceVariant else MaterialTheme.colorScheme.error
            )
        }
        Spacer(Modifier.height(Dimens.spacingXLarge))
        Row(verticalAlignment = Alignment.CenterVertically) {
            Column(Modifier.weight(1f)) {
                Text("Team Members", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.SemiBold)
                Text(
                    "Manage staff roles and location access.",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            Button(onClick = { creating = true }, enabled = !state.isSavingUser, shape = RoundedCornerShape(12.dp)) {
                Icon(Icons.Filled.Add, contentDescription = null, modifier = Modifier.size(18.dp))
                Spacer(Modifier.size(Dimens.spacingSmall))
                Text("Add member")
            }
        }
        Spacer(Modifier.height(Dimens.spacingMedium))
        Row(horizontalArrangement = Arrangement.spacedBy(Dimens.spacingSmall)) {
            FilterChip(selected = roleFilter == null, onClick = { roleFilter = null }, label = { Text("All") })
            FilterChip(selected = roleFilter == UserRole.MANAGER, onClick = { roleFilter = UserRole.MANAGER }, label = { Text("Managers") })
            FilterChip(selected = roleFilter == UserRole.STAFF, onClick = { roleFilter = UserRole.STAFF }, label = { Text("Staff") })
        }
        Spacer(Modifier.height(Dimens.spacingMedium))
        when {
            state.isUsersLoading -> Box(
                modifier = Modifier.fillMaxWidth().heightIn(min = 160.dp),
                contentAlignment = Alignment.Center
            ) { LoadingIndicator() }
            state.usersError != null -> ErrorBanner(state.usersError!!)
            filtered.isEmpty() -> EmptyTeamCard(onAdd = { creating = true })
            else -> Column(verticalArrangement = Arrangement.spacedBy(Dimens.spacingMedium)) {
                filtered.forEach { user ->
                    TeamMemberCard(
                        user = user,
                        locationNames = state.locations.filter { it.id in user.locationIds }.map { it.name },
                        onEdit = { editing = user },
                        onToggleActive = { confirmToggle = user },
                        onDelete = { confirmDelete = user }
                    )
                }
            }
        }
        Spacer(Modifier.height(Dimens.spacingXLarge))
    }

    if (creating) {
        TeamMemberFormDialog(
            initial = null, locations = state.locations,
            onDismiss = { creating = false },
            onSubmit = { onSave(it); creating = false },
            isSaving = state.isSavingUser
        )
    }
    editing?.let { user ->
        TeamMemberFormDialog(
            initial = user, locations = state.locations,
            onDismiss = { editing = null },
            onSubmit = { onSave(it); editing = null },
            isSaving = state.isSavingUser
        )
    }
    confirmToggle?.let { user ->
        val deactivating = user.isActive
        AlertDialog(
            onDismissRequest = { confirmToggle = null },
            icon = { Icon(if (deactivating) Icons.Outlined.Block else Icons.Outlined.Power, contentDescription = null) },
            title = { Text(if (deactivating) "Deactivate ${user.displayName}?" else "Reactivate ${user.displayName}?") },
            text = { Text(if (deactivating) "They will no longer be able to sign in." else "Their access will be restored.") },
            confirmButton = {
                Button(
                    onClick = { onSetActive(user.id, !user.isActive); confirmToggle = null },
                    colors = if (deactivating) ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.error,
                        contentColor = MaterialTheme.colorScheme.onError
                    ) else ButtonDefaults.buttonColors()
                ) { Text(if (deactivating) "Deactivate" else "Reactivate") }
            },
            dismissButton = { TextButton(onClick = { confirmToggle = null }) { Text("Cancel") } }
        )
    }
    confirmDelete?.let { user ->
        AlertDialog(
            onDismissRequest = { confirmDelete = null },
            icon = { Icon(Icons.Filled.Delete, contentDescription = null, tint = MaterialTheme.colorScheme.error) },
            title = { Text("Delete ${user.displayName}?") },
            text = { Text("This permanently removes their account and all access. This cannot be undone.") },
            confirmButton = {
                Button(
                    onClick = { onDelete(user.id); confirmDelete = null },
                    colors = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.error,
                        contentColor = MaterialTheme.colorScheme.onError
                    )
                ) { Text("Delete") }
            },
            dismissButton = { TextButton(onClick = { confirmDelete = null }) { Text("Cancel") } }
        )
    }
}

@Composable
private fun TeamMemberCard(
    user: User,
    locationNames: List<String>,
    onEdit: () -> Unit,
    onToggleActive: () -> Unit,
    onDelete: () -> Unit
) {
    var showMenu by remember { mutableStateOf(false) }
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant)
    ) {
        Row(modifier = Modifier.padding(Dimens.paddingMedium), verticalAlignment = Alignment.CenterVertically) {
            Box(
                modifier = Modifier.size(44.dp).background(
                    color = if (user.isActive) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.outlineVariant,
                    shape = CircleShape
                ),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = initialsOfAdmin(user.displayName, user.email),
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.SemiBold,
                    color = if (user.isActive) MaterialTheme.colorScheme.onPrimaryContainer
                            else MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            Spacer(Modifier.size(Dimens.spacingMedium))
            Column(Modifier.weight(1f)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        text = user.displayName.ifBlank { user.email.substringBefore('@') },
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.SemiBold,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f, fill = false)
                    )
                    Spacer(Modifier.size(6.dp))
                    RoleBadge(user.role)
                    if (!user.isActive) { Spacer(Modifier.size(4.dp)); InactiveBadge() }
                }
                Text(user.email, style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant, maxLines = 1, overflow = TextOverflow.Ellipsis)
                if (!user.phone.isNullOrBlank()) {
                    Text(user.phone, style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant)
                }
                if (locationNames.isNotEmpty()) {
                    Spacer(Modifier.height(4.dp))
                    Text(
                        text = locationNames.joinToString(" · "),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.primary,
                        maxLines = 1, overflow = TextOverflow.Ellipsis
                    )
                }
            }
            Box {
                IconButton(onClick = { showMenu = true }) {
                    Icon(Icons.Filled.MoreVert, contentDescription = "Options")
                }
                DropdownMenu(expanded = showMenu, onDismissRequest = { showMenu = false }) {
                    DropdownMenuItem(
                        text = { Text("Edit") },
                        leadingIcon = { Icon(Icons.Filled.Edit, contentDescription = null) },
                        onClick = { showMenu = false; onEdit() }
                    )
                    DropdownMenuItem(
                        text = { Text(if (user.isActive) "Deactivate" else "Reactivate") },
                        leadingIcon = { Icon(if (user.isActive) Icons.Outlined.Block else Icons.Outlined.Power, contentDescription = null) },
                        onClick = { showMenu = false; onToggleActive() }
                    )
                    DropdownMenuItem(
                        text = { Text("Delete", color = MaterialTheme.colorScheme.error) },
                        leadingIcon = { Icon(Icons.Filled.Delete, contentDescription = null, tint = MaterialTheme.colorScheme.error) },
                        onClick = { showMenu = false; onDelete() }
                    )
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun TeamMemberFormDialog(
    initial: User?,
    locations: List<Location>,
    onDismiss: () -> Unit,
    onSubmit: (User) -> Unit,
    isSaving: Boolean
) {
    val isEdit = initial != null
    var displayName by remember { mutableStateOf(initial?.displayName.orEmpty()) }
    var email by remember { mutableStateOf(initial?.email.orEmpty()) }
    var phone by remember { mutableStateOf(initial?.phone.orEmpty()) }
    var role by remember { mutableStateOf(initial?.role ?: UserRole.STAFF) }
    var selectedLocationIds by remember { mutableStateOf(initial?.locationIds?.toSet() ?: emptySet<String>()) }
    var isActive by remember { mutableStateOf(initial?.isActive ?: true) }
    val isValid = displayName.isNotBlank() && email.isNotBlank()

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(if (isEdit) "Edit Team Member" else "Add Team Member") },
        text = {
            Column(
                modifier = Modifier.verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(Dimens.spacingMedium)
            ) {
                OutlinedTextField(value = displayName, onValueChange = { displayName = it },
                    label = { Text("Display Name") }, singleLine = true, modifier = Modifier.fillMaxWidth())
                OutlinedTextField(value = email, onValueChange = { if (!isEdit) email = it },
                    label = { Text("Email") }, singleLine = true, enabled = !isEdit, modifier = Modifier.fillMaxWidth())
                OutlinedTextField(
                    value = phone, onValueChange = { phone = it },
                    label = { Text("Mobile Number") }, placeholder = { Text("e.g. +1 555 123 4567") },
                    singleLine = true, keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                    modifier = Modifier.fillMaxWidth()
                )
                Text("Role", style = MaterialTheme.typography.labelMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
                Row(horizontalArrangement = Arrangement.spacedBy(Dimens.spacingSmall)) {
                    UserRole.entries.filter { it != UserRole.OWNER }.forEach { r ->
                        FilterChip(selected = role == r, onClick = { role = r },
                            label = { Text(r.name.lowercase().replaceFirstChar { it.uppercase() }) })
                    }
                }
                if (locations.isNotEmpty()) {
                    Text("Location Access", style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant)
                    locations.forEach { loc ->
                        val checked = loc.id in selectedLocationIds
                        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                            Checkbox(
                                checked = checked,
                                onCheckedChange = {
                                    selectedLocationIds = if (checked) selectedLocationIds - loc.id
                                                        else selectedLocationIds + loc.id
                                }
                            )
                            Text(loc.name, style = MaterialTheme.typography.bodyMedium)
                        }
                    }
                }
                if (isEdit) {
                    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                        Text("Active", style = MaterialTheme.typography.bodyMedium, modifier = Modifier.weight(1f))
                        Switch(checked = isActive, onCheckedChange = { isActive = it })
                    }
                }
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    onSubmit(
                        (initial ?: User()).copy(
                            displayName = displayName.trim(),
                            email = email.trim(),
                            phone = phone.trim().ifBlank { null },
                            role = role,
                            locationIds = selectedLocationIds.toList(),
                            isActive = isActive
                        )
                    )
                },
                enabled = isValid && !isSaving
            ) { Text(if (isEdit) "Save" else "Add") }
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text("Cancel") } }
    )
}

@Composable
private fun RoleBadge(role: UserRole) {
    val bg = when (role) {
        UserRole.OWNER   -> MaterialTheme.colorScheme.tertiaryContainer
        UserRole.MANAGER -> MaterialTheme.colorScheme.primaryContainer
        UserRole.STAFF   -> MaterialTheme.colorScheme.secondaryContainer
    }
    val fg = when (role) {
        UserRole.OWNER   -> MaterialTheme.colorScheme.onTertiaryContainer
        UserRole.MANAGER -> MaterialTheme.colorScheme.onPrimaryContainer
        UserRole.STAFF   -> MaterialTheme.colorScheme.onSecondaryContainer
    }
    Box(modifier = Modifier.background(bg, RoundedCornerShape(999.dp)).padding(horizontal = 8.dp, vertical = 2.dp)) {
        Text(role.name.lowercase().replaceFirstChar { it.uppercase() },
            style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.SemiBold, color = fg)
    }
}

@Composable
private fun InactiveBadge() {
    Box(modifier = Modifier.background(MaterialTheme.colorScheme.errorContainer, RoundedCornerShape(999.dp)).padding(horizontal = 8.dp, vertical = 2.dp)) {
        Text("Inactive", style = MaterialTheme.typography.labelSmall,
            fontWeight = FontWeight.SemiBold, color = MaterialTheme.colorScheme.onErrorContainer)
    }
}

@Composable
private fun EmptyTeamCard(onAdd: () -> Unit) {
    Card(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant)
    ) {
        Column(modifier = Modifier.fillMaxWidth().padding(Dimens.paddingXLarge),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Icon(Icons.Outlined.Group, contentDescription = null,
                modifier = Modifier.size(40.dp), tint = MaterialTheme.colorScheme.onSurfaceVariant)
            Spacer(Modifier.height(Dimens.spacingMedium))
            Text("No team members yet", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.SemiBold)
            Spacer(Modifier.height(Dimens.spacingSmall))
            Text("Add staff or managers to give them access to this app.",
                style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
            Spacer(Modifier.height(Dimens.spacingMedium))
            Button(onClick = onAdd, shape = RoundedCornerShape(12.dp)) {
                Icon(Icons.Filled.Add, contentDescription = null, modifier = Modifier.size(18.dp))
                Spacer(Modifier.size(Dimens.spacingSmall))
                Text("Add first member")
            }
        }
    }
}
